import { computed, watch } from 'vue'

/* Plugins */
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import { useGtm } from '@gtm-support/vue-gtm'
import { useTranslation } from 'i18next-vue'
import uniqBy from 'lodash/uniqBy'
import pickBy from 'lodash/pickBy'
import { secondsToDuration, durationToString, durationToISO8601 } from '@helpers/duration'

/* Utils */
import { buildURL } from '@helpers/media'

/* Assets */
import logoHorizontal from '@assets/logo-horizontal.svg'

export default function (workType, jsonType, overrideFunction = null) {
  const store = useStore()
  const route = useRoute()
  const gtm = useGtm()
  const { t } = useTranslation()

  const locale = computed(() => store.getters['Global/locale'])
  const loading = computed(() => store.getters['Work/loading'])
  const getItemByTypeAndSlugOrId = (params) => store.getters['Work/getItemByTypeAndSlugOrId'](params)
  const getMetaByTypeAndSlugOrId = (params) => store.getters['Work/getMetaByTypeAndSlugOrId'](params)

  const itemParams = computed(() => {
    const params = {
      type: workType
    }

    if (route.params.slug) {
      params.slug = route.params.slug
    } else if (route.params.id) {
      params.id = route.params.id
    }

    return params
  })

  const loadByTypeAndSlugOrId = () => store.dispatch('Work/loadByTypeAndSlugOrId', itemParams.value)

  const item = computed(() => getItemByTypeAndSlugOrId(itemParams.value))

  const meta = computed(() => getMetaByTypeAndSlugOrId(itemParams.value))

  const genreToString = overrideFunction?.genreToString ?? computed(() => {
    if (!item.value.genres && !item.value.topics) {
      return false
    }

    return uniqBy(item.value.genres).join(', ') || uniqBy(item.value.topics).join(', ')
  })

  const duration = computed(() => secondsToDuration(item.value.duration))

  const durationString = overrideFunction?.durationString ?? computed(() => durationToString(duration.value))

  const durationISO = computed(() => durationToISO8601(duration.value))

  const directors = computed(() => {
    if (!item.value.directors) {
      return false
    }

    return uniqBy(item.value.directors, director => director.slug) // prevent unwanted duplicates
  })

  const producers = computed(() => {
    if (!item.value.producers) {
      return false
    }

    return uniqBy(item.value.producers, producer => producer.slug) // prevent unwanted duplicates
  })

  const writers = computed(() => {
    if (!item.value.writers) {
      return false
    }

    return uniqBy(item.value.writers, writer => writer.slug) // prevent unwanted duplicates
  })

  const directorsToString = computed(() => directors.value ? directors.value.map(item => `${item.first_name} ${item.last_name}`).join(', ') : '')

  const producersToString = computed(() => producers.value ? producers.value.map(item => `${item.first_name} ${item.last_name}`).join(', ') : '')

  const writersToString = computed(() => writers.value ? writers.value.map(item => `${item.first_name} ${item.last_name}`).join(', ') : '')

  const subjectsToString = computed(() => {
    if (!item.value.subjects || !item.value.subjects.length) {
      return ''
    }

    return item.value.subjects.map(subject => {
      let string = subject.name
      if (subject.children?.length) {
        string = `${string} > ${subject.children.map(item => item.name).join(', ')}`
      }
      return string
    }).join(' | ')
  })

  const legendList = computed(() => {
    if (!item.value) {
      return []
    }

    const items = []

    if (item.value.year) {
      items.push({ html: item.value.year })
    }

    if (duration.value) {
      items.push({ html: durationString.value })
    }

    let categoryGenres = t(`category.${item.value.category}`)
    if (genreToString.value) {
      categoryGenres += `: ${genreToString.value}`
    }
    items.push({
      html: categoryGenres
    })

    return items
  })

  const infoProps = computed(() => {
    if (!item.value) {
      return {}
    }

    return {
      title: item.value.title,
      legendList: legendList.value,
      shortCredits: {
        directors: directors.value,
        producers: producers.value,
        writers: writers.value
      },
      description: item.value.description,
      mediaList: item.value.thumbnail ? [item.value.thumbnail] : [],
      mediaPattern: meta.value.media.thumbnail_url,
      rating: item.value.rating,
      warning: item.value.warning,
      cc: item.value.cc,
      dv: item.value.dv,
      productionModel: item.value.production_model,
      agencyNote: item.value.agency_note
    }
  })

  const dataLayerParams = computed(() => {
    const params = {
      workTitle: item.value.title,
      workYear: item.value.year,
      workGenre: genreToString.value,
      workType,
      workCreator: directorsToString.value,
      workProducer: producersToString.value,
      workScript: writersToString.value,
      workRunningTime: durationString.value,
      workSubject: subjectsToString.value,
      section: 'collection',
      language: locale.value
    }

    return pickBy(params, param => param)
  })

  const jsonLdThumbnail = computed(() => {
    // placeholder
    if (!item.value.thumbnail) {
      return logoHorizontal
    }

    return buildURL(meta.value.media.thumbnail_url, { width: 300, path: item.value.thumbnail })
  })

  const jsonLd = computed(() => {
    if (!item.value) {
      return {}
    }

    const json = {
      '@context': ['http://schema.org', { '@language': locale.value }],
      '@type': jsonType,
      '@id': typeof window === 'undefined' ? null : window.location.origin + route.fullPath,
      url: typeof window === 'undefined' ? null : window.location.origin + route.fullPath,
      name: item.value.title,
      abstract: item.value.description,
      dateCreated: item.value.year,
      director: directors.value.map(director => ({ '@type': 'Person', name: `${director.first_name} ${director.last_name}` })),
      image: jsonLdThumbnail.value,
      sameAs: item.value.availability?.resource_url
    }

    if (item.value.duration) {
      json.duration = durationISO.value
    }

    return json
  })

  const jsonLdToString = computed(() => JSON.stringify(jsonLd.value))

  const metaTitle = computed(() => {
    if (!item.value) {
      return t(`workCategory.${workType}`)
    }

    return item.value.title
  })

  const metaLinks = computed(() => {
    return item.value?.availability?.resource_url
      ? [{ rel: 'canonical', href: item.value.availability.resource_url }]
      : []
  })

  const sendDataLayer = () => {
    if (gtm && gtm.enabled() && typeof window !== 'undefined') {
      window.dataLayer?.push(dataLayerParams)
    }
  }

  watch(item, () => {
    if (item.value) {
      sendDataLayer()
    }
  })

  watch(locale, loadByTypeAndSlugOrId)

  if (!item.value) loadByTypeAndSlugOrId()

  return {
    metaTitle,
    metaLinks,
    jsonLdToString,
    item,
    loading,
    infoProps,
    meta,
    duration,
    legendList,
    loadByTypeAndSlugOrId
  }
}
